import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, Eye, Target, Compass, Heart, Shield, Users } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const coreValues = [{
  icon: Heart,
  title: 'Integrity',
  desc: 'Upholding the highest standards of honesty, transparency, and accountability in all our operations.'
}, {
  icon: Users,
  title: 'Teamwork',
  desc: 'Fostering collaboration across directorates, agencies, and stakeholders for collective impact.'
}, {
  icon: Target,
  title: 'Results-Oriented',
  desc: 'Focusing on measurable outcomes that directly improve the livelihoods of Ugandan farmers.'
}, {
  icon: Shield,
  title: 'Professionalism',
  desc: 'Delivering services with competence, diligence, and adherence to established standards.'
}, {
  icon: Compass,
  title: 'Innovation',
  desc: 'Embracing modern technologies and approaches to transform agricultural productivity.'
}, {
  icon: Eye,
  title: 'Inclusiveness',
  desc: 'Ensuring equitable access to agricultural services for all citizens regardless of gender, age, or location.'
}];
export function VisionMissionPage() {
  return <>
      <Header data-id="element-1659" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-1660">
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-1661">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&h=600&fit=crop")'
        }} data-id="element-1662"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-1663"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-1664">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-1665">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-1666">
                <Home className="w-4 h-4 mr-1" data-id="element-1667" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1668" />
              <span data-id="element-1669">About MAAIF</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1670" />
              <span className="text-white font-medium" data-id="element-1671">Vision & Mission</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1672">
              Vision & Mission
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-1673">
              Guiding principles and strategic direction of the Ministry of
              Agriculture, Animal Industry and Fisheries.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-1674">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1675">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8" data-id="element-1676">
              <div className="w-14 h-14 rounded-xl bg-uganda-green flex items-center justify-center mb-6" data-id="element-1677">
                <Eye className="w-7 h-7 text-white" data-id="element-1678" />
              </div>
              <h2 className="text-2xl font-bold text-uganda-black mb-4" data-id="element-1679">
                Our Vision
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg" data-id="element-1680">
                "A competitive, profitable, and sustainable agricultural
                sector."
              </p>
              <p className="text-gray-500 mt-4 leading-relaxed" data-id="element-1681">
                We envision a transformed agricultural sector that ensures food
                and nutrition security, creates employment, and generates wealth
                for all Ugandans through modern, market-oriented, and
                environmentally sustainable practices.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8" data-id="element-1682">
              <div className="w-14 h-14 rounded-xl bg-uganda-yellow flex items-center justify-center mb-6" data-id="element-1683">
                <Target className="w-7 h-7 text-white" data-id="element-1684" />
              </div>
              <h2 className="text-2xl font-bold text-uganda-black mb-4" data-id="element-1685">
                Our Mission
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg" data-id="element-1686">
                "To transform subsistence farming to commercial agriculture."
              </p>
              <p className="text-gray-500 mt-4 leading-relaxed" data-id="element-1687">
                The Ministry is mandated to promote and support sustainable and
                market-oriented agricultural production, food security, and
                household incomes through formulation and review of policies,
                regulation, coordination, inspection, monitoring, and evaluation
                of the agricultural sector.
              </p>
            </motion.div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-16" data-id="element-1688">
            <h2 className="text-2xl font-bold text-uganda-black mb-4" data-id="element-1689">
              Mandate
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-1690">
              The Ministry of Agriculture, Animal Industry and Fisheries (MAAIF)
              is the Government of Uganda ministry responsible for agriculture,
              animal husbandry, and fisheries. Its mandate includes:
            </p>
            <ul className="space-y-3" data-id="element-1691">
              {['Formulating, reviewing, and implementing national policies, plans, and strategies for the agricultural sector.', 'Supporting the production, productivity, and marketing of agricultural products.', 'Controlling and managing crop pests and diseases, and animal diseases.', 'Regulating and ensuring quality of agricultural inputs and products.', 'Promoting agricultural research, extension, and education.', 'Managing fisheries resources and promoting aquaculture development.', 'Coordinating the activities of semi-autonomous agencies under the ministry.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1692">
                  <span className="w-2 h-2 bg-uganda-green rounded-full mt-2 flex-shrink-0" data-id="element-1693" />
                  <span className="text-gray-600" data-id="element-1694">{item}</span>
                </li>)}
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1695">
            Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1696">
            {coreValues.map((value, index) => {
            const Icon = value.icon;
            return <motion.div key={value.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.08
            }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1697">
                  <div className="w-10 h-10 rounded-lg bg-uganda-light-green text-uganda-green flex items-center justify-center mb-4" data-id="element-1698">
                    <Icon className="w-5 h-5" data-id="element-1699" />
                  </div>
                  <h3 className="font-bold text-lg text-uganda-black mb-2" data-id="element-1700">
                    {value.title}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-1701">{value.desc}</p>
                </motion.div>;
          })}
          </div>
        </div>
      </main>
      <Footer data-id="element-1702" />
    </>;
}